import React, { useEffect, useRef } from 'react';
import { Phone, MapPin, Facebook, Star } from 'lucide-react';

const Header = () => {
  const headerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <header ref={headerRef} className="relative min-h-screen flex flex-col justify-center items-center px-4 py-20 overflow-hidden">
      {/* Car silhouette background */}
      <div className="car-silhouette"></div>
      
      <div className="relative z-10 text-center max-w-6xl mx-auto">
        {/* Main Title - Logo Image */}
        <div className="mb-6 md:mb-8 px-4 flex justify-center">
          <img 
            src="/images/logo.png" 
            alt="MIAL CAR SERVICE" 
            translate="no"
            className="logo-image w-full max-w-2xl md:max-w-4xl lg:max-w-5xl h-auto object-contain"
            style={{ imageRendering: 'crisp-edges' }}
          />
        </div>
        
        {/* Subtitle */}
        <div className="mb-8 md:mb-12 space-y-1 md:space-y-2 px-4">
          <h2 className="font-rajdhani text-xl sm:text-2xl md:text-4xl font-bold text-neon-pink">
            Michał Szelągowski
          </h2>
          <h3 className="font-rajdhani text-lg sm:text-xl md:text-2xl font-semibold text-neon-pink opacity-90">
            Mechanika Pojazdowa
          </h3>
        </div>
        
        {/* Contact Info */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12 max-w-4xl mx-auto px-4">
          {/* Phone */}
          <div className="flex flex-col items-center md:items-end space-y-2">
            <div className="flex items-center space-x-3 md:space-x-4">
              <Phone className="w-6 h-6 md:w-8 md:h-8 flex-shrink-0" style={{ color: '#f215e0' }} />
              <a 
                href="tel:605056541" 
                className="font-rajdhani text-xl sm:text-2xl md:text-3xl font-bold text-white transition-all duration-300"
                style={{ hover: { color: '#f215e0' } }}
                onMouseEnter={(e) => e.target.style.color = '#f215e0'}
                onMouseLeave={(e) => e.target.style.color = 'white'}
              >
                605 056 541
              </a>
            </div>
            <a 
              href="https://wa.me/48605056541" 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 font-rajdhani text-sm md:text-base text-cyan-400 font-semibold hover:text-cyan-300 transition-all duration-300"
            >
              <img 
                src="/images/whatsapp.png" 
                alt="WhatsApp" 
                className="w-5 h-5 md:w-6 md:h-6"
              />
              <span>RÓWNIEŻ KONTAKT PRZEZ WHATS APP</span>
            </a>
          </div>
          
          {/* Address */}
          <div className="flex items-center justify-center md:justify-start space-x-3 md:space-x-4">
            <MapPin className="w-6 h-6 md:w-8 md:h-8 text-cyan-400 flex-shrink-0" />
            <div className="font-rajdhani text-lg sm:text-xl md:text-2xl font-bold text-white text-center md:text-left">
              <div>86-260 Unisław</div>
              <div>ul. Głażewska 18</div>
            </div>
          </div>
        </div>
        
        {/* Facebook Button */}
        <div className="flex justify-center mb-12 md:mb-16 px-4">
          <a
            href="https://www.facebook.com/p/Mial-Car-Service-61554223497200/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-neon flex items-center space-x-2 md:space-x-3 text-base md:text-lg hover:scale-105 transform transition-all duration-300"
          >
            <Facebook className="w-5 h-5 md:w-6 md:h-6" />
            <span>Odwiedź nas na Facebooku</span>
          </a>
        </div>

        {/* Hours Section - Moved from bottom */}
        <div className="glass-morphism rounded-2xl p-6 md:p-8 lg:p-10 max-w-2xl mx-auto w-full">
          <h2 className="font-passion text-2xl md:text-3xl lg:text-4xl font-bold text-cyan-400 mb-6 md:mb-8 text-center">
            GODZINY OTWARCIA
          </h2>
          
          <div className="space-y-3 md:space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-center p-3 md:p-4 rounded-xl bg-black/30 border border-pink-500/20 hover:border-pink-500/40 transition-all duration-300" style={{ borderColor: 'rgba(242, 21, 224, 0.2)' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(242, 21, 224, 0.4)'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(242, 21, 224, 0.2)'}>
              <span className="font-rajdhani text-base md:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-0 text-center sm:text-left">
                Poniedziałek - Piątek
              </span>
              <span className="font-rajdhani text-base md:text-lg lg:text-xl font-bold" style={{ color: '#f215e0' }}>
                09:00 – 21:00
              </span>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between items-center p-3 md:p-4 rounded-xl bg-black/30 border border-pink-500/20 hover:border-pink-500/40 transition-all duration-300" style={{ borderColor: 'rgba(242, 21, 224, 0.2)' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(242, 21, 224, 0.4)'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(242, 21, 224, 0.2)'}>
              <span className="font-rajdhani text-base md:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-0 text-center sm:text-left">
                Sobota
              </span>
              <span className="font-rajdhani text-base md:text-lg lg:text-xl font-bold" style={{ color: '#f215e0' }}>
                09:00 – 18:00
              </span>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between items-center p-3 md:p-4 rounded-xl bg-black/30 border border-pink-500/20 hover:border-pink-500/40 transition-all duration-300" style={{ borderColor: 'rgba(242, 21, 224, 0.2)' }} onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(242, 21, 224, 0.4)'} onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(242, 21, 224, 0.2)'}>
              <span className="font-rajdhani text-base md:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-0 text-center sm:text-left">
                Niedziela
              </span>
              <span className="font-rajdhani text-base md:text-lg lg:text-xl font-bold" style={{ color: '#f215e0' }}>
                10:00 – 14:00
              </span>
            </div>
          </div>
          
          <div className="mt-6 md:mt-8 p-3 md:p-4 rounded-xl bg-gradient-to-r border" style={{ backgroundImage: 'linear-gradient(to right, rgba(242, 21, 224, 0.1), rgba(34, 211, 238, 0.1))', borderColor: 'rgba(242, 21, 224, 0.3)' }}>
            <p className="font-rajdhani text-sm md:text-base text-gray-300 leading-relaxed text-center">
              <span style={{ color: '#f215e0' }} className="font-bold">Zadzwoń</span> lub <span className="text-cyan-400 font-bold">odwiedź nas</span> w przypadku awarii!<br/>
              Profesjonalne usługi w mechanice pojazdowej.
            </p>
          </div>
        </div>

        {/* Services Section */}
        <div className="glass-morphism rounded-2xl p-6 md:p-8 lg:p-10 max-w-2xl mx-auto w-full mt-12 md:mt-16">
          <h2 className="font-passion text-2xl md:text-3xl lg:text-4xl font-bold text-cyan-400 mb-6 md:mb-8 text-center">
            MOŻLIWE USŁUGI
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-4 p-3 md:p-4 rounded-xl bg-black/30 border border-pink-500/20 transition-all duration-300" style={{ borderColor: 'rgba(242, 21, 224, 0.2)' }}>
              <Star className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0" style={{ fill: '#f215e0', color: '#f215e0' }} />
              <span className="font-rajdhani text-lg md:text-xl font-semibold text-white">
                diagnostyka
              </span>
            </div>

            <div className="flex items-center space-x-4 p-3 md:p-4 rounded-xl bg-black/30 border border-pink-500/20 transition-all duration-300" style={{ borderColor: 'rgba(242, 21, 224, 0.2)' }}>
              <Star className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0" style={{ fill: '#f215e0', color: '#f215e0' }} />
              <span className="font-rajdhani text-lg md:text-xl font-semibold text-white">
                analiza
              </span>
            </div>

            <div className="flex items-center space-x-4 p-3 md:p-4 rounded-xl bg-black/30 border border-pink-500/20 transition-all duration-300" style={{ borderColor: 'rgba(242, 21, 224, 0.2)' }}>
              <Star className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0" style={{ fill: '#f215e0', color: '#f215e0' }} />
              <span className="font-rajdhani text-lg md:text-xl font-semibold text-white">
                serwis olejowy
              </span>
            </div>

            <div className="flex items-center space-x-4 p-3 md:p-4 rounded-xl bg-black/30 border border-pink-500/20 transition-all duration-300" style={{ borderColor: 'rgba(242, 21, 224, 0.2)' }}>
              <Star className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0" style={{ fill: '#f215e0', color: '#f215e0' }} />
              <span className="font-rajdhani text-lg md:text-xl font-semibold text-white">
                naprawy główne i bieżące
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
